import { useState } from 'react';
import { Modal, Form, Input, Select, Button, Space, Typography, message } from 'antd';
import { api } from '../../api/client';

const { Text } = Typography;
const { TextArea } = Input;

export interface CorrectionModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill the source context so the user only needs to enter the new value + reason */
  source: {
    module: string;
    entity: string;
    recordId: number;
    recordLabel?: string;
  } | null;
  /** If provided, limits which fields can be corrected */
  correctableFields?: Array<{ key: string; label: string; currentValue: unknown }>;
  onSuccess?: () => void;
}

export function CorrectionModal({
  open,
  onClose,
  source,
  correctableFields,
  onSuccess,
}: CorrectionModalProps) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [selectedField, setSelectedField] = useState<string | null>(null);

  const currentField = correctableFields?.find((f) => f.key === selectedField);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!source) return;

      setSubmitting(true);
      await api.post('/corrections', {
        sourceModule: source.module,
        sourceEntity: source.entity,
        sourceRecordId: source.recordId,
        field: values.field,
        oldValue: currentField?.currentValue ?? values.oldValue,
        newValue: values.newValue,
        reason: values.reason,
      });

      message.success('Correction request submitted for approval');
      form.resetFields();
      setSelectedField(null);
      onClose();
      onSuccess?.();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      const msg = err instanceof Error ? err.message : 'Failed to submit correction';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedField(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="Request Correction"
      width={520}
      footer={
        <Space>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="primary" loading={submitting} onClick={handleSubmit}>
            Submit for Approval
          </Button>
        </Space>
      }
      destroyOnClose
    >
      {source && (
        <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fafafa', borderRadius: 6 }}>
          <Text type="secondary">
            {source.module} / {source.entity} #{source.recordId}
            {source.recordLabel && <> &mdash; {source.recordLabel}</>}
          </Text>
        </div>
      )}

      <Form form={form} layout="vertical">
        {correctableFields ? (
          <Form.Item name="field" label="Field to Correct" rules={[{ required: true, message: 'Select a field' }]}>
            <Select
              placeholder="Select field"
              onChange={(val) => setSelectedField(val)}
              options={correctableFields.map((f) => ({ label: f.label, value: f.key }))}
            />
          </Form.Item>
        ) : (
          <Form.Item name="field" label="Field Name" rules={[{ required: true, message: 'Enter field name' }]}>
            <Input placeholder="e.g. unitPrice" />
          </Form.Item>
        )}

        {currentField && (
          <Form.Item label="Current Value">
            <Input value={String(currentField.currentValue ?? '')} disabled />
          </Form.Item>
        )}

        {!correctableFields && (
          <Form.Item name="oldValue" label="Current Value" rules={[{ required: true, message: 'Enter current value' }]}>
            <Input placeholder="Current value of the field" />
          </Form.Item>
        )}

        <Form.Item name="newValue" label="Correct Value" rules={[{ required: true, message: 'Enter the correct value' }]}>
          <Input placeholder="What it should be" />
        </Form.Item>

        <Form.Item
          name="reason"
          label="Reason for Correction"
          rules={[{ required: true, message: 'Explain why this needs correction (min 10 chars)' }, { min: 10, message: 'Minimum 10 characters' }]}
        >
          <TextArea rows={3} placeholder="Explain why this value is wrong and what happened..." />
        </Form.Item>
      </Form>
    </Modal>
  );
}
